// Sampler periodico de RTCStats pra consulta em videochamada. Le os stats
// brutos do RTCPeerConnection (via track.sender/track.receiver), guarda a
// amostra anterior e calcula delta-based rates: packet loss %, bitrate kbps,
// jitter ms, freezes, etc.
//
// O resultado e estruturado em uma lista de metricas, uma por
// direcao+kind+participante. Ex:
//   { direction: 'outbound', kind: 'audio', packetsLostPct: 0.3, bitrateKbps: 42 }
//   { direction: 'inbound',  kind: 'video', packetsLostPct: 2.1, bitrateKbps: 480,
//     jitterMs: 18, framesDropped: 4, framesPerSecond: 23, identity: 'host-abc' }
//
// Pra usar: mantenha uma ref pro ultimo `rawSnapshot()` e passe pro
// `computeDeltaMetrics(prev, curr, deltaMs)` no proximo tick. O helper tambem
// expoe `summarizeSeverity(metrics)` que decide info/warn/error baseado no
// pior metric da rodada - evita logar tudo quando a rede ta normal.

const LIVEKIT = {
  // Track.Source enum values like "camera", "microphone", "screen_share",
  // "screen_share_audio". Guardamos como string.
  video: ['camera', 'screen_share'],
  audio: ['microphone', 'screen_share_audio'],
};

/**
 * Snapshot bruto de stats agrupado por chave (direcao+kind+identity+source).
 * Inclui um timestamp pra poder calcular delta no proximo tick.
 *
 * Alem dos stats por track, captura informacao de CONTEXTO da conexao:
 *  - publisherPair / subscriberPair: RTT e banda estimada extraidas do
 *    candidate-pair ativo (o caminho ICE que esta realmente carregando
 *    pacotes). Publisher e subscriber tem PCs separados no LiveKit, por
 *    isso ficam em campos distintos.
 *  - navigatorConnection: dica do proprio navegador sobre o tipo de rede
 *    (4g/3g/wifi) e downlink estimado em Mbps. Bem suportado em Android
 *    Chrome; iOS Safari nao expoe.
 */
export async function rawSnapshot(room) {
  if (!room) {
    return { ts: Date.now(), entries: new Map(), publisherPair: null, subscriberPair: null, navigatorConnection: captureNavigatorConnection() };
  }

  const entries = new Map();
  let publisherReport = null;
  let subscriberReport = null;

  // Outbound (o que este cliente esta enviando para o SFU). Pega stats do
  // sender de cada publication local.
  try {
    for (const pub of room.localParticipant.trackPublications.values()) {
      const sender = pub.track?.sender;
      if (!sender?.getStats) continue;
      const report = await sender.getStats();
      if (!publisherReport) publisherReport = report; // guarda pra extrair candidate-pair
      report.forEach(stat => {
        if (stat.type !== 'outbound-rtp') return;
        const kind = normalizeKind(stat.kind || pub.kind);
        if (!kind) return;
        const key = `out|${kind}|self|${pub.source}`;
        // Simulcast: varias outbound-rtp por pub (uma por camada). Somamos.
        const prev = entries.get(key);
        // qualityLimitationReason: "none" | "bandwidth" | "cpu" | "other".
        // Qualquer camada do simulcast limitada por CPU contamina a key toda:
        // se uma camada ta throttleando o encoder, o frame final ja chega
        // degradado - conta como CPU pra todo o kind. "bandwidth" so vence
        // se nao tiver CPU (menor prioridade pra UX: avisar CPU e mais
        // acionavel que avisar rede caindo).
        const incomingLimit = stat.qualityLimitationReason || null;
        const prevLimit = prev?.qualityLimitation || null;
        let mergedLimit = prevLimit;
        if (incomingLimit === 'cpu') mergedLimit = 'cpu';
        else if (incomingLimit === 'bandwidth' && mergedLimit !== 'cpu') mergedLimit = 'bandwidth';
        else if (incomingLimit && incomingLimit !== 'none' && !mergedLimit) mergedLimit = incomingLimit;
        entries.set(key, {
          direction: 'outbound',
          kind,
          source: pub.source,
          identity: 'self',
          packetsSent: (prev?.packetsSent || 0) + (stat.packetsSent || 0),
          bytesSent: (prev?.bytesSent || 0) + (stat.bytesSent || 0),
          retransmitted: (prev?.retransmitted || 0) + (stat.retransmittedPacketsSent || 0),
          framesSent: (prev?.framesSent || 0) + (stat.framesSent || 0),
          nackCount: (prev?.nackCount || 0) + (stat.nackCount || 0),
          qualityLimitation: mergedLimit,
        });
      });
    }
  } catch { /* sem publisher ainda ou SDK nao expoe getStats */ }

  // Inbound (o que este cliente esta recebendo de cada participante remoto).
  try {
    for (const [, p] of room.remoteParticipants) {
      for (const pub of p.trackPublications.values()) {
        const receiver = pub.track?.receiver;
        if (!receiver?.getStats) continue;
        const report = await receiver.getStats();
        if (!subscriberReport) subscriberReport = report;
        report.forEach(stat => {
          if (stat.type !== 'inbound-rtp') return;
          const kind = normalizeKind(stat.kind || pub.kind);
          if (!kind) return;
          const key = `in|${kind}|${p.identity}|${pub.source}`;
          entries.set(key, {
            direction: 'inbound',
            kind,
            source: pub.source,
            identity: p.identity,
            participantName: p.name || p.identity,
            packetsReceived: stat.packetsReceived || 0,
            packetsLost: stat.packetsLost || 0,
            bytesReceived: stat.bytesReceived || 0,
            jitter: stat.jitter || 0,                 // seconds
            framesDecoded: stat.framesDecoded || 0,
            framesDropped: stat.framesDropped || 0,
            freezeCount: stat.freezeCount || 0,
            totalFreezeSeconds: stat.totalFreezesDuration || 0,
            nackCount: stat.nackCount || 0,
            pliCount: stat.pliCount || 0,            // pedido de keyframe
          });
        });
      }
    }
  } catch { /* participantes ainda nao assinaram nada */ }

  return {
    ts: Date.now(),
    entries,
    publisherPair: extractActivePair(publisherReport),
    subscriberPair: extractActivePair(subscriberReport),
    navigatorConnection: captureNavigatorConnection(),
  };
}

/**
 * Retorna um objeto de contexto com RTT, banda estimada e tipo de rede da
 * amostra atual. Nao tem nocao de delta - e snapshot do "estado agora". Pode
 * vir com campos null quando o navegador nao expoe (ex: iOS Safari).
 */
export function extractContext(curr) {
  if (!curr) return null;
  return {
    publisherRttMs: curr.publisherPair?.rttMs ?? null,
    subscriberRttMs: curr.subscriberPair?.rttMs ?? null,
    availableOutgoingKbps: curr.publisherPair?.availableOutgoingKbps ?? null,
    availableIncomingKbps: curr.subscriberPair?.availableIncomingKbps ?? null,
    navigator: curr.navigatorConnection ?? null,
  };
}

/**
 * Dada a snapshot atual e a anterior, calcula metricas de rede: bitrate,
 * packet loss %, jitter, fps. Retorna um array de linhas prontas pra logar
 * ou renderizar. Ignora linhas com delta zerado (evita logar sala em silencio).
 */
export function computeDeltaMetrics(prev, curr) {
  if (!prev || !curr || prev.ts === curr.ts) return [];
  const deltaSec = Math.max((curr.ts - prev.ts) / 1000, 0.001);
  const rows = [];

  for (const [key, c] of curr.entries) {
    const p = prev.entries.get(key);
    if (!p) continue; // track novo, sem base de comparacao

    if (c.direction === 'outbound') {
      const packetsSentDelta = (c.packetsSent - p.packetsSent) | 0;
      const bytesSentDelta = Math.max(c.bytesSent - p.bytesSent, 0);
      if (packetsSentDelta <= 0 && bytesSentDelta <= 0) continue;
      rows.push({
        direction: 'outbound',
        kind: c.kind,
        source: c.source,
        identity: c.identity,
        bitrateKbps: Math.round((bytesSentDelta * 8) / deltaSec / 1000),
        packetsSent: packetsSentDelta,
        retransmitted: Math.max((c.retransmitted || 0) - (p.retransmitted || 0), 0),
        framesPerSecond: c.kind === 'video'
          ? Math.round(((c.framesSent - p.framesSent) / deltaSec) * 10) / 10
          : null,
        // "cpu" = encoder limitado por CPU (medico em aparelho lento, muita
        // coisa aberta). "bandwidth" = upload saturado. "other" = razao
        // desconhecida do SDK/codec. Nao propaga quando e "none" ou ausente.
        qualityLimitation: c.qualityLimitation && c.qualityLimitation !== 'none' ? c.qualityLimitation : null,
      });
    } else {
      const packetsReceivedDelta = Math.max(c.packetsReceived - p.packetsReceived, 0);
      const packetsLostDelta = Math.max(c.packetsLost - p.packetsLost, 0);
      const bytesReceivedDelta = Math.max(c.bytesReceived - p.bytesReceived, 0);
      if (packetsReceivedDelta === 0 && packetsLostDelta === 0 && bytesReceivedDelta === 0) continue;
      const denom = packetsReceivedDelta + packetsLostDelta;
      const lossPct = denom > 0 ? Math.round((packetsLostDelta / denom) * 1000) / 10 : 0;
      rows.push({
        direction: 'inbound',
        kind: c.kind,
        source: c.source,
        identity: c.identity,
        participantName: c.participantName,
        bitrateKbps: Math.round((bytesReceivedDelta * 8) / deltaSec / 1000),
        packetsReceived: packetsReceivedDelta,
        packetsLost: packetsLostDelta,
        packetsLostPct: lossPct,
        jitterMs: Math.round((c.jitter || 0) * 1000),
        framesPerSecond: c.kind === 'video'
          ? Math.round(((c.framesDecoded - p.framesDecoded) / deltaSec) * 10) / 10
          : null,
        framesDropped: c.kind === 'video' ? Math.max(c.framesDropped - p.framesDropped, 0) : null,
        freezeCount: c.kind === 'video' ? Math.max(c.freezeCount - p.freezeCount, 0) : null,
      });
    }
  }

  return rows;
}

/**
 * Decide severity (info/warn/error) a partir do pior metric. Usado pra
 * escalonar o nivel do DiagnosticEvent antes de mandar pro backend - assim
 * um sample saudavel nao polui o painel de "Erros (1h)" do admin.
 */
export function summarizeSeverity(metrics) {
  let severity = 'info';
  for (const m of metrics) {
    if (m.direction === 'inbound' && typeof m.packetsLostPct === 'number') {
      if (m.packetsLostPct >= 10) return 'error';
      if (m.packetsLostPct >= 2) severity = 'warn';
    }
    if (m.direction === 'inbound' && m.kind === 'video' && (m.freezeCount || 0) > 2) {
      severity = severity === 'info' ? 'warn' : severity;
    }
    // CPU throttle do encoder e sintoma acionavel pelo medico (fechar abas,
    // baixar qualidade). Vale no minimo um warn pra aparecer no painel.
    if (m.direction === 'outbound' && m.kind === 'video' && m.qualityLimitation === 'cpu') {
      severity = severity === 'info' ? 'warn' : severity;
    }
  }
  return severity;
}

function normalizeKind(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase();
  if (s === 'audio' || LIVEKIT.audio.includes(s)) return 'audio';
  if (s === 'video' || LIVEKIT.video.includes(s)) return 'video';
  return null;
}

// Encontra o candidate-pair ativo (nominated + state=succeeded) em um
// StatsReport. Retorna RTT em ms e banda estimada em kbps. Esses numeros
// vem do ICE/WebRTC e descrevem o caminho real da conexao, nao a saude
// individual de cada track.
function extractActivePair(report) {
  if (!report) return null;
  let active = null;
  report.forEach(stat => {
    if (stat.type !== 'candidate-pair') return;
    if (stat.nominated && (stat.state === 'succeeded' || stat.state === undefined)) {
      active = stat;
    }
  });
  if (!active) return null;
  return {
    rttMs: typeof active.currentRoundTripTime === 'number'
      ? Math.round(active.currentRoundTripTime * 1000)
      : null,
    availableOutgoingKbps: typeof active.availableOutgoingBitrate === 'number'
      ? Math.round(active.availableOutgoingBitrate / 1000)
      : null,
    availableIncomingKbps: typeof active.availableIncomingBitrate === 'number'
      ? Math.round(active.availableIncomingBitrate / 1000)
      : null,
  };
}

// NetworkInformation API: Android Chrome e Chrome desktop expoem; iOS Safari
// nao. Quando disponivel, traz um palpite do tipo de rede do aparelho que
// ajuda a explicar o que ta acontecendo ("4g fraco" vs "wifi estavel").
function captureNavigatorConnection() {
  if (typeof navigator === 'undefined') return null;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return null;
  return {
    effectiveType: c.effectiveType || null,                               // 'slow-2g' | '2g' | '3g' | '4g'
    downlinkMbps: typeof c.downlink === 'number' ? c.downlink : null,     // estimativa de banda em Mbps
    rttMs: typeof c.rtt === 'number' ? c.rtt : null,                      // estimativa de RTT em ms
    saveData: !!c.saveData,                                                // usuario ligou o "economizar dados"
    type: c.type || null,                                                  // 'wifi' | 'cellular' | ... (menos suportado)
  };
}
